// ForecastScreen.jsx
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useSelector } from "react-redux";
import DataLoader from "../components/DataLoader";
import {
    Alert,
    AlertTitle,
    Button,
    CircularProgress,
    Container,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import DateRangePicker from "../components/DateRangePicker";
import ToggleButtonTitled from "../components/ToggleButtonTitled";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Axios from "axios";
import { DEFAULT_HOTEL, SanitizeHotelID, URLAPI } from "../configuration";
import ReportTableMetricsSummary from "../components/shared/ReportBuilder/ReportTableMetricsSummary";
import EChartsReact from "echarts-for-react";
import { HotelSelector } from "../components/TopBar";
import ConfirmModal from "../components/ConfirmModal";
import {
    COMPARISON_SAME_TIME_LAST_YEAR,
    COMPARISON_FILTER,
    COMPARISON_PREVIOUS_YEAR,
    DATATYPE_RESDATE,
    process_,
    GROUP_DATE_YEAR,
    GROUP_DATE_MONTH,
    GROUP_DATE_DAY,
} from "../helpers/filters";
import { MIN_DATE, MAX_DATE } from "../helpers/dates";
import { TARGET_OFF } from "../helpers/targets";
import { process } from "../helpers/reports";

import { fns } from "../helpers/common";
import SmallPercentage from "../components/SmallPercentage";

dayjs.extend(utc);
const formatChartDate = (value) =>
    dayjs.utc(value).locale("en").format("DD MMM");

const REVENUE = "REVENUE";
const BOOKINGS = "BOOKINGS";

const TARGET_COLOR = "#fac657";
const OTB_COLOR = "#5470c7";
const PREDICTED_COLOR = "#90cc74";

const AUTO_MODEL = "auto";
const CUSTOM_PRESET_VALUE = "CUSTOM";

const GREEN_COLOR = "#16a34a";
const RED_COLOR = "#dc2626";

const TARGET_MODE_PERCENTAGE = "percentage";
const TARGET_MODE_MANUAL = "manual";
const TARGET_MODE_COMBINED = "combined";

const MANUAL_TARGETS_URL = `${URLAPI}/manual_targets`;

const isLoadedLike = (slice) =>
    slice &&
    (slice.status === "loaded" || slice.status === "partiallyLoaded") &&
    Array.isArray(slice.data);

export const buildManualInterpolatedSeries = ({
    dayKeys,
    manualTargetsByDay,
}) => {
    const safeDayKeys = Array.isArray(dayKeys) ? dayKeys : [];
    const keys = Object.keys(manualTargetsByDay || {}).sort();
    if (keys.length === 0) return safeDayKeys.map(() => null);

    const anchors = keys
        .map((k) => ({
            k,
            idx: safeDayKeys.indexOf(k),
            v: Number(manualTargetsByDay[k]),
        }))
        .filter((a) => a.idx >= 0 && Number.isFinite(a.v))
        .sort((a, b) => a.idx - b.idx);

    if (anchors.length === 0) return safeDayKeys.map(() => null);

    const out = new Array(safeDayKeys.length).fill(null);

    if (anchors.length === 1) return out.map(() => Math.round(anchors[0].v));

    for (let i = 0; i < anchors[0].idx; i++) out[i] = Math.round(anchors[0].v);
    for (let i = anchors[anchors.length - 1].idx; i < out.length; i++)
        out[i] = Math.round(anchors[anchors.length - 1].v);

    for (let a = 0; a < anchors.length - 1; a++) {
        const left = anchors[a];
        const right = anchors[a + 1];
        const span = right.idx - left.idx;

        out[left.idx] = Math.round(left.v);
        out[right.idx] = Math.round(right.v);

        for (let i = left.idx + 1; i < right.idx; i++) {
            const t = (i - left.idx) / span;
            out[i] = Math.round(left.v + (right.v - left.v) * t);
        }
    }

    return out;
};

export const pickAutoForecastModel = (availableModels) => {
    const preference = [
        "catboost",
        "pycaret_auto",
        "hist_gbr",
        "tweedie",
        "poisson",
        "naive_zero",
    ];
    const set = new Set((availableModels || []).map((m) => String(m)));
    for (const m of preference) if (set.has(m)) return m;
    return (availableModels && availableModels[0]) || "naive_zero";
};

export const fetchForecastPrediction = async ({
    urlApi,
    authToken,
    siteId,
    modelName,
    startDateStr,
    horizonDays,
    objective,
}) => {
    const params = {
        SiteID: Number(siteId),
        model_name: modelName,
        start_date: startDateStr,
        horizon_days: Number(horizonDays),
        objective,
    };

    const resp = await Axios.get(`${urlApi}/AI/predict/bookings/predict`, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        },
        params,
    });

    const payload = resp?.data?.data ?? resp?.data ?? {};
    const rows = Array.isArray(payload?.predictions) ? payload.predictions : [];
    return { rows, params: payload?.params ?? params };
};

export const buildForecastPredictedByDay = ({ predictRows }) => {
    const out = {};
    for (const row of predictRows || []) {
        const rawDate = row?.ResDate ?? row?.date ?? row?.Date ?? row?.ds;
        if (!rawDate) continue;

        const k = dayjs.utc(rawDate).format("YYYY-MM-DD");
        const v = Number(row?.y_pred ?? row?.y_pred_original ?? row?.value);
        out[String(k)] = Number.isFinite(v) ? v : 0;
    }
    return out;
};

export const getForecastMetricValueFromBooking = ({ metric, row }) => {
    // metric expected: "REVENUE" | "BOOKINGS"
    if (metric === "REVENUE") {
        const raw =
            row?.TotalPrice ?? row?.totalPrice ?? row?.Price ?? row?.price;
        const v = Number(raw);
        return Number.isFinite(v) ? v : 0;
    }
    return 1;
};

export const buildActualByResDateDay = ({
    rows,
    fromKey,
    toKey,
    metric,
    onlySpecialOffer = true,
}) => {
    const out = {};
    const start = dayjs.utc(fromKey).startOf("day").valueOf();
    const end = dayjs.utc(toKey).startOf("day").valueOf();

    for (const d of rows || []) {
        const date_ = dayjs.utc(d.ResDate).startOf("day").valueOf();
        if (date_ < start || date_ > end) continue;

        if (onlySpecialOffer && d.Type !== "Special Offer") continue;

        const key = dayjs.utc(d.ResDate).format("YYYY-MM-DD");
        out[key] =
            (out[key] ?? 0) +
            getForecastMetricValueFromBooking({ metric, row: d });
    }

    return out;
};

export const mapLastYearToCurrentDay = ({ dayKeys, lastYearByDay }) => {
    const out = {};
    for (const k of dayKeys || []) {
        const lyKey = dayjs.utc(k).subtract(1, "year").format("YYYY-MM-DD");
        out[k] = Number(lastYearByDay?.[lyKey] ?? 0);
    }
    return out;
};

export const buildPercentTargetSeries = ({
    dayKeys,
    lastYearMappedToCurrentDay,
    targetPct,
}) => {
    const mul = 1 + Number(targetPct || 0) / 100;
    return (dayKeys || []).map((k) => {
        const ly = Number(lastYearMappedToCurrentDay?.[k] ?? 0);
        return Math.round(ly * mul);
    });
};

export const buildCombinedTargetSeries = ({
    dayKeys,
    manualTargetsByDay,
    percentTargetSeries,
}) => {
    return (dayKeys || []).map((k, i) => {
        const anchor = manualTargetsByDay?.[k];
        if (anchor != null && Number.isFinite(Number(anchor))) {
            return Math.round(Number(anchor));
        }
        return Array.isArray(percentTargetSeries) ? percentTargetSeries[i] : 0;
    });
};

export const buildTargetSeriesByDay = ({
    dayKeys,
    targetMode,
    manualInterpolatedSeries,
    combinedTargetSeries,
    percentTargetSeries,
}) => {
    const base =
        targetMode === "manual"
            ? manualInterpolatedSeries
            : targetMode === "combined"
              ? combinedTargetSeries
              : percentTargetSeries;

    return (dayKeys || []).map((_, i) => {
        const v = Array.isArray(base) ? base[i] : undefined;
        return v === null ? 0 : Number(v || 0);
    });
};

export const sumByDayKeys = ({ dayKeys, perDayMap }) => {
    let sum = 0;
    for (const k of dayKeys || []) sum += Number(perDayMap?.[k] ?? 0);
    return sum;
};

export const roundForecastMetricTotal = ({ metric, value }) => {
    const v = Number(value);
    if (!Number.isFinite(v)) return 0;
    return metric === "REVENUE" ? Number(v.toFixed(2)) : Math.round(v);
};

export const variancePct = ({ base, value }) => {
    const b = Number(base);
    const v = Number(value);
    if (!Number.isFinite(b) || b <= 0) return NaN;
    if (!Number.isFinite(v)) return NaN;
    return ((v - b) / b) * 100;
};

const ForecastScreen = () => {
    const dataBookings = useSelector((state) => state.dataBookings);
    const dataSimple = useSelector((state) => state.dataSimple);
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const { auth } = useSelector((state) => state);

    const [metric, setMetric] = useState(BOOKINGS);
    const [forecastPreset, setForecastPreset] = useState("initial");
    const forecastPresetOptions = useMemo(() => {
        const base = dayjs.utc();

        const buildMonth = (offset, value = null) => {
            const m = base.add(offset, "month");
            return {
                value: value ? value : `M${offset}`,
                label: m.format("MMMM"),
                getRange: () => {
                    const monthStart = m.startOf("month").startOf("day");
                    const monthEnd = m.endOf("month").startOf("day");
                    return { from: monthStart, to: monthEnd };
                },
            };
        };

        return [buildMonth(0, "initial"), buildMonth(1), buildMonth(2)];
    }, []);

    const todayUtc = dayjs.utc().startOf("day");
    const minForecastUtc = todayUtc.startOf("month");
    const maxForecastUtc = dayjs.utc().add(100, "day").startOf("day");

    const [fromDate, setFromDate] = useState(minForecastUtc);
    const [toDate, setToDate] = useState(
        todayUtc.endOf("month").startOf("day"),
    );

    const showHistoryActual = useMemo(() => {
        // NOTE: This controls the "existing" line.
        // We show it whenever Bookings data is available.
        // (User-facing name is now "OTB" - On the books)
        return isLoadedLike(dataBookings);
    }, [dataBookings]);

    const [targetPct, setTargetPct] = useState(5);
    const [comparison, setComparison] = useState(COMPARISON_PREVIOUS_YEAR);
    const [targetMode, setTargetMode] = useState(TARGET_MODE_PERCENTAGE);
    const [isTargetEditMode, setIsTargetEditMode] = useState(false);

    const [isTargetConfirmOpen, setIsTargetConfirmOpen] = useState(false);
    const [pendingTargetAction, setPendingTargetAction] = useState(null);
    const [draftTargetMarker, setDraftTargetMarker] = useState(null);
    const draftTargetMarkerRef = useRef(null);

    const [manualTargetsByDay, setManualTargetsByDay] = useState({});
    const [isLoadingManualTargets, setIsLoadingManualTargets] = useState(false);
    const [manualTargetsError, setManualTargetsError] = useState(null);

    const chartRef = useRef(null);

    const [availableModels, setAvailableModels] = useState([]);
    const [isLoadingModels, setIsLoadingModels] = useState(false);
    const [modelsError, setModelsError] = useState(null);

    const [modelName, setModelName] = useState(AUTO_MODEL);
    const [selectedSiteId, setSelectedSiteId] = useState("");

    const [isPredicting, setIsPredicting] = useState(false);
    const [needsPredict, setNeedsPredict] = useState(true);
    const [predictError, setPredictError] = useState(null);

    const [predictCache, setPredictCache] = useState({});
    const [predictRows, setPredictRows] = useState([]);

    const [trainedHotels, setTrainedHotels] = useState(null);
    const [isLoadingTrainedHotels, setIsLoadingTrainedHotels] = useState(false);
    const [trainedHotelsError, setTrainedHotelsError] = useState(null);

    const autoPredictedRef = useRef(null);

    const currentHotelKey = useMemo(() => {
        const raw = SanitizeHotelID(hotelID);
        return String(raw ?? "").trim();
    }, [hotelID]);

    const currentHotelSiteIDs = useMemo(() => {
        const h = (dataSimple?.data?.Hotels || []).find(
            (it) => String(it?.HotelID) === currentHotelKey,
        );

        if (h?.SiteIDs) {
            return String(h.SiteIDs)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
        }

        // Fall back to reservation SiteIDs when the hotel mapping is unavailable.
        if (
            !isLoadedLike(dataBookings) ||
            String(dataBookings.hotelID) !== currentHotelKey
        )
            return [];

        return [
            ...new Set(
                dataBookings.data
                    .map((d) => String(d.SiteID ?? "").trim())
                    .filter(Boolean),
            ),
        ];
    }, [dataSimple, dataBookings, currentHotelKey]);

    useEffect(() => {
        if (!currentHotelSiteIDs || currentHotelSiteIDs.length === 0) {
            setSelectedSiteId("");
            return;
        }
        if (!currentHotelSiteIDs.includes(String(selectedSiteId))) {
            setSelectedSiteId(String(currentHotelSiteIDs[0]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(currentHotelSiteIDs)]);
    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            try {
                setIsLoadingTrainedHotels(true);
                setTrainedHotelsError(null);

                const resp = await Axios.get(
                    `${URLAPI}/AI/predict/bookings/list_hotels`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${auth?.user?.jwt ?? ""}`,
                        },
                    },
                );

                const payload = resp?.data?.data ?? resp?.data ?? {};
                const hotels = Array.isArray(payload?.hotels)
                    ? payload.hotels
                    : [];

                if (!cancelled) setTrainedHotels(hotels);
            } catch (e) {
                if (!cancelled) {
                    setTrainedHotelsError(
                        e?.message ||
                            "Failed to load forecast trained hotels list",
                    );
                    setTrainedHotels(null);
                }
            } finally {
                if (!cancelled) setIsLoadingTrainedHotels(false);
            }
        };

        if (auth?.user?.jwt) run();

        return () => {
            cancelled = true;
        };
    }, [auth?.user?.jwt]); // eslint-disable-line react-hooks/exhaustive-deps

    const trainedSiteIDsSet = useMemo(() => {
        const set = new Set();

        if (!Array.isArray(trainedHotels)) return set;

        (trainedHotels || []).forEach((h) => {
            // API may return {site, name} or plain values. Be tolerant.
            if (h && typeof h === "object") {
                if (h.site != null) set.add(String(h.site).trim());
                else if (Array.isArray(h.sites))
                    h.sites.forEach(
                        (s) => s != null && set.add(String(s).trim()),
                    );
                else if (h.SiteIDs != null)
                    String(h.SiteIDs)
                        .split(",")
                        .forEach((s) => s && set.add(String(s).trim()));
            } else if (h != null) {
                set.add(String(h).trim());
            }
        });

        return set;
    }, [trainedHotels]);

    const siteIdByHotelId = useMemo(() => {
        const hotels = dataSimple?.data?.Hotels || [];
        const out = {};

        hotels.forEach((h) => {
            const hid = String(h?.HotelID ?? "");
            if (!hid) return;

            const raw = h?.SiteIDs ?? "";
            const siteIDs = String(raw)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);

            const preferred = siteIDs.find((sid) =>
                trainedSiteIDsSet.has(String(sid)),
            );

            out[hid] = preferred || siteIDs[0] || "";
        });

        return out;
    }, [dataSimple, trainedSiteIDsSet]);

    const hotelsAllowedIds = useMemo(() => {
        if (!Array.isArray(trainedHotels) || trainedHotels.length === 0)
            return null;

        const hotels = dataSimple?.data?.Hotels || [];
        const allowed = [];

        hotels.forEach((h) => {
            const hid = h?.HotelID;
            if (hid == null) return;

            const raw = h?.SiteIDs ?? "";
            const siteIDs = String(raw)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);

            const isAllowed = siteIDs.some((sid) =>
                trainedSiteIDsSet.has(String(sid)),
            );
            if (isAllowed) allowed.push(String(hid));
        });

        // Grand Budapest uses "default" visually, but predicts as DEFAULT_HOTEL.
        if (allowed.includes(String(DEFAULT_HOTEL))) {
            allowed.push("default");
        }

        return [...new Set(allowed)];
    }, [trainedHotels, dataSimple, trainedSiteIDsSet]);

    const isCurrentHotelTrained = useMemo(() => {
        if (!Array.isArray(trainedHotels) || trainedHotels.length === 0)
            return true;
        if (!currentHotelSiteIDs || currentHotelSiteIDs.length === 0)
            return true;

        return currentHotelSiteIDs.some((sid) =>
            trainedSiteIDsSet.has(String(sid)),
        );
    }, [trainedHotels, currentHotelSiteIDs, trainedSiteIDsSet]);

    const requestStartUtc = useMemo(() => {
        return todayUtc;
    }, [todayUtc]);

    const forecastVisibleStartUtc = useMemo(() => {
        const fd = dayjs.utc(fromDate).startOf("day");
        return fd.isAfter(todayUtc, "day") ? fd : todayUtc;
    }, [fromDate, todayUtc]);

    const startDateStr = useMemo(
        () => dayjs.utc(requestStartUtc).format("YYYY-MM-DD"),
        [requestStartUtc],
    );

    // Forecast horizon: requestStartUtc..toDate (inclusive)
    const horizonDays = useMemo(() => {
        const start = dayjs.utc(requestStartUtc).startOf("day");
        const end = dayjs.utc(toDate).startOf("day");
        const diff = end.diff(start, "day") + 1;
        return Math.max(1, Math.min(365, diff));
    }, [requestStartUtc, toDate]);

    // Forecast day keys (used for Period summary + predicted totals)
    const dayKeys = useMemo(() => {
        const start = dayjs.utc(fromDate).startOf("day");
        const end = dayjs.utc(toDate).startOf("day");
        const arr = [];

        for (let d = start; !d.isAfter(end, "day"); d = d.add(1, "day")) {
            arr.push(d.format("YYYY-MM-DD"));
        }

        return arr;
    }, [fromDate, toDate]);

    const targetType = "forecast"; // if you want to share with Pickup, set "pickup"
    const targetSubType = useMemo(() => {
        return metric === REVENUE ? "revenue" : "bookings";
    }, [metric]);

    const hotelKeyForTargets = useMemo(() => {
        const raw = SanitizeHotelID(hotelID);
        return Number(raw);
    }, [hotelID]);

    const userIdForTargets = auth?.user?.user_id;

    const fromKeyForTargets = dayjs.utc(fromDate).format("YYYY-MM-DD");

    const toKeyForTargets = useMemo(() => {
        return dayjs.utc(toDate).format("YYYY-MM-DD");
    }, [toDate]);

    const fetchManualTargets = useCallback(async () => {
        if (!auth?.user?.jwt) return;
        if (!Number.isFinite(hotelKeyForTargets)) return;

        try {
            setIsLoadingManualTargets(true);
            setManualTargetsError(null);

            const resp = await Axios.get(MANUAL_TARGETS_URL, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${auth?.user?.jwt ?? ""}`,
                },
                params: {
                    hotel_id: hotelKeyForTargets,
                    target_type: targetType,
                    target_subtype: targetSubType,
                    date_granularity: "DAY",
                    from_date: fromKeyForTargets,
                    to_date: toKeyForTargets,
                },
            });

            const rows = resp?.data?.data ?? resp?.data ?? [];
            const map = {};
            (Array.isArray(rows) ? rows : []).forEach((r) => {
                const k = dayjs.utc(r.TargetDate).format("YYYY-MM-DD");
                const v = Number(r.TargetValue);
                if (k && Number.isFinite(v)) map[k] = v;
            });

            setManualTargetsByDay(map);
        } catch (e) {
            setManualTargetsByDay({});
            setManualTargetsError(
                e?.message || "Failed to load manual targets",
            );
        } finally {
            setIsLoadingManualTargets(false);
        }
    }, [
        auth?.user?.jwt,
        hotelKeyForTargets,
        targetType,
        targetSubType,
        fromKeyForTargets,
        toKeyForTargets,
    ]);

    const upsertManualTargetPoint = useCallback(
        async (dateKey, value) => {
            if (!userIdForTargets) return;

            await Axios.post(
                MANUAL_TARGETS_URL,
                {
                    hotel_id: hotelKeyForTargets,
                    target_date: dateKey,
                    target_type: targetType,
                    target_subtype: targetSubType,
                    target_value: value,
                    date_granularity: "DAY",
                    user_id: userIdForTargets,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${auth?.user?.jwt ?? ""}`,
                    },
                },
            );

            setManualTargetsByDay((prev) => ({ ...prev, [dateKey]: value }));
        },
        [
            auth?.user?.jwt,
            hotelKeyForTargets,
            targetType,
            targetSubType,
            userIdForTargets,
        ],
    );

    const deleteManualTargetPoint = useCallback(
        async (dateKey) => {
            await Axios.delete(MANUAL_TARGETS_URL, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${auth?.user?.jwt ?? ""}`,
                },
                params: {
                    hotel_id: hotelKeyForTargets,
                    target_date: dateKey,
                    date_granularity: "DAY",
                    target_type: targetType,
                    target_subtype: targetSubType,
                },
            });

            setManualTargetsByDay((prev) => {
                const next = { ...prev };
                delete next[dateKey];
                return next;
            });
        },
        [auth?.user?.jwt, hotelKeyForTargets, targetType, targetSubType],
    );

    useEffect(() => {
        if (targetMode === TARGET_MODE_PERCENTAGE) return;
        fetchManualTargets();
    }, [targetMode, fetchManualTargets]);

    // Booking window for chart: last month + forecast range
    const historyStart = useMemo(() => {
        return dayjs.utc(fromDate).startOf("day");
    }, [fromDate]);

    const historyEnd = useMemo(() => {
        return dayjs.utc(toDate).startOf("day");
    }, [toDate]);

    const chartStart = useMemo(() => {
        return dayjs.utc(fromDate).startOf("day");
    }, [fromDate]);

    const chartDayKeys = useMemo(() => {
        const start = dayjs.utc(chartStart).startOf("day");
        const end = dayjs.utc(toDate).startOf("day");
        const arr = [];
        for (let d = start; !d.isAfter(end, "day"); d = d.add(1, "day")) {
            arr.push(d.format("YYYY-MM-DD"));
        }
        return arr;
    }, [chartStart, toDate]);

    const autoModelResolved = useMemo(
        () => pickAutoForecastModel(availableModels),
        [availableModels],
    );

    const modelNameResolved = useMemo(() => {
        return modelName === AUTO_MODEL ? autoModelResolved : modelName;
    }, [modelName, autoModelResolved]);

    const objective = metric === REVENUE ? "revenue" : "bookings";
    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            try {
                setIsLoadingModels(true);
                setModelsError(null);

                const resp = await Axios.get(
                    `${URLAPI}/AI/predict/bookings/list`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${auth?.user?.jwt ?? ""}`,
                        },
                        params: { objective },
                    },
                );

                const payload = resp?.data?.data ?? resp?.data ?? {};
                const models = Array.isArray(payload?.available_models)
                    ? payload.available_models
                    : [];

                if (!cancelled) setAvailableModels(models);
            } catch (e) {
                if (!cancelled)
                    setModelsError(
                        e?.message || "Failed to load forecast models list",
                    );
            } finally {
                if (!cancelled) setIsLoadingModels(false);
            }
        };

        if (auth?.user?.jwt) run();
        return () => {
            cancelled = true;
        };
    }, [auth?.user?.jwt, objective]);

    useEffect(() => {
        if (modelName === AUTO_MODEL) return;
        const set = new Set((availableModels || []).map((m) => String(m)));
        if (availableModels.length > 0 && !set.has(String(modelName))) {
            setModelName(AUTO_MODEL);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(availableModels)]);

    const cacheKey = useMemo(() => {
        const sid = String(selectedSiteId || "");
        const m = String(modelNameResolved || "");
        return `${objective}|${sid}|${m}|${startDateStr}|${Number(
            horizonDays,
        )}`;
    }, [
        objective,
        selectedSiteId,
        modelNameResolved,
        startDateStr,
        horizonDays,
    ]);
    useEffect(() => {
        // Allow auto-predict again when the key changes
        autoPredictedRef.current = false;
        setPredictError(null);
    }, [cacheKey]);
    const hasCachedForCurrent = Boolean(predictCache[cacheKey]?.rows?.length);

    useEffect(() => {
        if (hasCachedForCurrent) {
            setPredictRows(predictCache[cacheKey].rows);
            setNeedsPredict(false);
        } else {
            setPredictRows([]);
            setNeedsPredict(true);
        }
    }, [cacheKey, hasCachedForCurrent, predictCache]);

    const handlePredict = async () => {
        try {
            setPredictError(null);

            const siteIdNum = Number(selectedSiteId);
            if (!Number.isFinite(siteIdNum) || siteIdNum <= 0) {
                setPredictError(
                    "No valid SiteID found for this hotel. Check SimpleData -> Hotels -> SiteIDs.",
                );
                return;
            }

            if (!modelNameResolved) {
                setPredictError("No model selected.");
                return;
            }

            setIsPredicting(true);

            const { rows, params } = await fetchForecastPrediction({
                urlApi: URLAPI,
                authToken: auth?.user?.jwt ?? "",
                siteId: siteIdNum,
                modelName: modelNameResolved,
                startDateStr,
                horizonDays,
                objective,
            });
            if (!rows.length)
                throw new Error(
                    "The API returned no predictions for the selected configuration.",
                );

            // keep variable name
            const payloadParams = params ?? {
                SiteID: siteIdNum,
                model_name: modelNameResolved,
                start_date: startDateStr,
                horizon_days: horizonDays,
                objective,
            };

            setPredictCache((prev) => ({
                ...prev,
                [cacheKey]: {
                    rows,
                    fetchedAt: dayjs.utc().toISOString(),
                    params: payloadParams,
                },
            }));

            setPredictRows(rows);
            setNeedsPredict(false);
        } catch (e) {
            setPredictRows([]);
            setNeedsPredict(true);

            const apiMsg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e?.message ||
                "Unknown error";
            const status = e?.response?.status
                ? ` (HTTP ${e.response.status})`
                : "";
            setPredictError(`Could not load forecast data${status}: ${apiMsg}`);
        } finally {
            setIsPredicting(false);
        }
    };

    useEffect(() => {
        if (autoPredictedRef.current === cacheKey) return;
        if (!needsPredict) return;
        if (isPredicting) return;
        if (!auth?.user?.jwt) return;
        if (!selectedSiteId) return;
        if (isLoadingModels) return;
        if (availableModels.length === 0) return;

        autoPredictedRef.current = cacheKey;
        handlePredict();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        needsPredict,
        isPredicting,
        auth?.user?.jwt,
        selectedSiteId,
        isLoadingModels,
        availableModels.length,
        cacheKey,
    ]);

    // -------------------------
    // Predictions map (forecast range)
    // -------------------------
    const predictedByDay = useMemo(() => {
        return buildForecastPredictedByDay({ predictRows });
    }, [predictRows]);

    // -------------------------
    // Historical (last month) actual bookings by ResDate date
    // -------------------------
    const metricValueFromBooking = (d) => {
        if (metric === REVENUE) {
            const raw = d?.TotalPrice ?? d?.totalPrice ?? d?.Price ?? d?.price;
            const v = Number(raw);
            return Number.isFinite(v) ? v : 0;
        }
        return 1; // BOOKINGS: count each booking as 1
    };
    const historyActualByDay = useMemo(() => {
        const out = {};
        if (!showHistoryActual) return out;
        if (!isLoadedLike(dataBookings)) return out;

        const start = dayjs.utc(historyStart).valueOf();
        const end = dayjs.utc(historyEnd).valueOf();

        for (const d of dataBookings.data) {
            const date_ = dayjs.utc(d.ResDate).startOf("day").valueOf();
            if (date_ < start || date_ > end) continue;

            if (d.Type !== "Special Offer") continue;

            const key = dayjs.utc(d.ResDate).format("YYYY-MM-DD");
            out[key] = (out[key] ?? 0) + metricValueFromBooking(d);
            out[key] =
                Number.isFinite(out[key]) && out[key] !== 0 ? out[key] : null;
        }

        return out;
    }, [showHistoryActual, dataBookings, historyStart, historyEnd, metric]);

    const roundMetric = (n) => {
        const v = Number(n);
        if (!Number.isFinite(v)) return 0;
        return metric === REVENUE ? Number(v.toFixed(2)) : Math.round(v);
    };

    const totalHistoryActual = useMemo(() => {
        if (!showHistoryActual) return 0;

        let sum = 0;
        const start = dayjs.utc(historyStart);
        const end = dayjs.utc(historyEnd);

        for (let d = start; !d.isAfter(end, "day"); d = d.add(1, "day")) {
            const k = d.format("YYYY-MM-DD");
            sum += Number(historyActualByDay[k] ?? 0);
        }
        return roundMetric(sum);
    }, [
        showHistoryActual,
        historyActualByDay,
        historyStart,
        historyEnd,
        metric,
    ]);

    const reportingLY = useMemo(() => {
        if (
            !isLoadedLike(dataBookings) ||
            String(dataBookings.hotelID) !== currentHotelKey
        )
            return null;

        const { entries } = process_({
            js: {
                dataType: DATATYPE_RESDATE,
                groupBy: [GROUP_DATE_YEAR, GROUP_DATE_MONTH, GROUP_DATE_DAY],
                fromDay: fromKeyForTargets,
                toDay: toKeyForTargets,
                fromArrivalDay: MIN_DATE,
                toArrivalDay: MAX_DATE,
                fromResDateDay: MIN_DATE,
                toResDateDay:
                    comparison === COMPARISON_SAME_TIME_LAST_YEAR
                        ? startDateStr
                        : MAX_DATE,
                filters: {
                    Comparison: comparison,
                    Cancellations: "included",
                    Target: TARGET_OFF,
                },
            },
            dataBookings,
            dataSimple,
            hotelID,
            processFn: (rows, id) =>
                process(rows, id, { roundRoomAndExtras: false }),
        });

        return entries;
    }, [
        dataBookings,
        dataSimple,
        hotelID,
        currentHotelKey,
        fromKeyForTargets,
        toKeyForTargets,
        startDateStr,
        comparison,
    ]);

    const lastYearMappedToCurrentDay = useMemo(() => {
        const out = {};

        for (const { keys, resultOneYearBefore } of Object.values(
            reportingLY ?? {},
        )) {
            if (resultOneYearBefore?.bookingsRooms == null) continue;

            const date = keys
                .map(([, value]) => String(value).padStart(2, "0"))
                .join("-");

            // Reporting already aligns comparison dates with the current year.
            const key = date < fromKeyForTargets ? fromKeyForTargets : date;

            out[key] =
                (out[key] ?? 0) +
                Number(
                    resultOneYearBefore[
                        metric === BOOKINGS
                            ? "bookingsRooms"
                            : "revenueRoomAndExtras"
                    ] ?? 0,
                );
        }

        return out;
    }, [reportingLY, fromKeyForTargets, metric]);

    const totalLastYearForForecastPeriod = useMemo(() => {
        let sum = 0;
        for (const k of dayKeys)
            sum += Number(lastYearMappedToCurrentDay[k] ?? 0);
        return roundMetric(sum);
    }, [dayKeys, lastYearMappedToCurrentDay, metric]);

    const totalHistoryActualLY = reportingLY
        ? totalLastYearForForecastPeriod
        : undefined;

    const totalPredicted = useMemo(() => {
        if (needsPredict) return null;

        let sum = 0;
        for (const k of dayKeys) {
            if (k >= startDateStr) sum += Number(predictedByDay[k] ?? 0);
        }

        return roundMetric(sum);
    }, [needsPredict, dayKeys, predictedByDay, metric, startDateStr]);

    const percentTargetSeries = useMemo(() => {
        const mul = 1 + Number(targetPct || 0) / 100;
        let totalLY = 0;

        return dayKeys.map((k) => {
            const previousTarget = Math.round(totalLY * mul);
            totalLY += Number(lastYearMappedToCurrentDay[k] ?? 0);

            // Keep the chart total equal to the period target.
            return Math.round(totalLY * mul) - previousTarget;
        });
    }, [dayKeys, lastYearMappedToCurrentDay, targetPct]);

    const manualInterpolatedSeries = useMemo(() => {
        return buildManualInterpolatedSeries({ dayKeys, manualTargetsByDay });
    }, [dayKeys, manualTargetsByDay]);

    const combinedTargetSeries = useMemo(() => {
        return dayKeys.map((k, i) => {
            const anchor = manualTargetsByDay?.[k];
            if (anchor != null && Number.isFinite(Number(anchor)))
                return Math.round(Number(anchor));
            return percentTargetSeries[i];
        });
    }, [dayKeys, manualTargetsByDay, percentTargetSeries]);

    const targetSeriesByDay = useMemo(() => {
        const base =
            targetMode === TARGET_MODE_MANUAL
                ? manualInterpolatedSeries
                : targetMode === TARGET_MODE_COMBINED
                  ? combinedTargetSeries
                  : percentTargetSeries;

        return dayKeys.map((_, i) => {
            const v = Array.isArray(base) ? base[i] : undefined;
            return v === null ? 0 : Number(v || 0);
        });
    }, [
        targetMode,
        manualInterpolatedSeries,
        combinedTargetSeries,
        percentTargetSeries,
        dayKeys,
    ]);

    const dayIdxByKey = useMemo(() => {
        const out = {};
        dayKeys.forEach((k, i) => {
            out[k] = i;
        });
        return out;
    }, [dayKeys]);

    const totalTarget = useMemo(() => {
        let sum = 0;
        for (const v of targetSeriesByDay) sum += Number(v || 0);
        return roundMetric(sum);
    }, [targetSeriesByDay, metric]);

    const metricLabel =
        metric === REVENUE
            ? "Revenue"
            : metric === BOOKINGS
              ? "Bookings"
              : "Room Nights";

    const actualLabel = `OTB ${metricLabel}`;
    const otbPlusPredicted = `OTB + Predicted ${metricLabel}`;
    const otbLabel = `OTB ${metricLabel}`;
    const predictedLabel = `Predicted ${metricLabel}`;
    const comparisonLabel =
        comparison === COMPARISON_SAME_TIME_LAST_YEAR
            ? "STLY"
            : COMPARISON_FILTER[comparison].label;

    const stlyLabel = `${comparisonLabel} ${metricLabel}`;
    const targetLabel = `Target ${metricLabel}`;
    const varianceStlyLabel = `Variance vs ${comparisonLabel} (%)`;
    const varianceTargetLabel = "Variance vs Target (%)";
    // -------------------------
    // Period summary table (includes last month actual + forecast period stuff)
    // -------------------------
    const summaryData = useMemo(() => {
        const predictedVal = Number(totalPredicted ?? 0);
        const totalVal = Number(totalHistoryActual ?? 0) + predictedVal;
        const stlyVal = totalHistoryActualLY ?? "—";
        const targetVal = Number(totalTarget ?? 0);

        const safeVariancePct = (predicted, base) => {
            const p = Number(predicted ?? 0);
            const b = Number(base ?? 0);

            if (!Number.isFinite(p) || !Number.isFinite(b)) return "—";
            if (b === 0) return p === 0 ? 0 : "—";

            return ((p - b) / b) * 100;
        };

        const varVsStlyPct = !needsPredict
            ? safeVariancePct(totalVal, stlyVal)
            : "—";

        const varVsTargetPct = !needsPredict
            ? safeVariancePct(totalVal, targetVal)
            : "—";

        const result = {
            [predictedLabel]: !needsPredict ? predictedVal : "—",
            [otbPlusPredicted]: !needsPredict ? totalVal : "—",
            [stlyLabel]: stlyVal,
            [targetLabel]: targetVal,
            [varianceStlyLabel]: varVsStlyPct,
            [varianceTargetLabel]: varVsTargetPct,
        };

        const resultOneYearBefore = {
            // Keeps your existing behavior: show STLY total under the same row as "Predicted"
            [predictedLabel]: totalLastYearForForecastPeriod,
        };

        if (showHistoryActual) {
            result[actualLabel] = totalHistoryActual ?? 0;
            resultOneYearBefore[actualLabel] = totalHistoryActualLY;
        }

        return {
            group0: { keys: [], result, resultOneYearBefore },
        };
    }, [
        showHistoryActual,
        totalHistoryActual,
        totalHistoryActualLY,
        needsPredict,
        totalPredicted,
        totalTarget,
        totalLastYearForForecastPeriod,
        metric,
        actualLabel,
        otbPlusPredicted,
        predictedLabel,
        stlyLabel,
        targetLabel,
        varianceStlyLabel,
        varianceTargetLabel,
    ]);

    const summaryJs = useMemo(() => {
        const fieldsOrder = [
            ...(showHistoryActual ? [actualLabel, otbPlusPredicted] : []),
            predictedLabel,
            stlyLabel,
            targetLabel,
            varianceStlyLabel,
            varianceTargetLabel,
        ];

        return {
            fromDay: dayjs.utc(fromDate).toISOString(),
            toDay: dayjs.utc(toDate).toISOString(),
            dataType: "FORECAST",
            filters: { Comparison: comparison },
            hideFields: [],
            fieldsOrder,
            hidePercentageFor: fieldsOrder,
        };
    }, [
        fromDate,
        toDate,
        showHistoryActual,
        actualLabel,
        otbPlusPredicted,
        predictedLabel,
        stlyLabel,
        targetLabel,
        varianceStlyLabel,
        varianceTargetLabel,
    ]);

    const weekendMarkAreas = useMemo(() => {
        const areas = [];
        const set = new Set(chartDayKeys);

        for (const k of chartDayKeys) {
            const d = dayjs.utc(k);
            // Friday
            if (d.day() !== 5) continue;

            const sat = d.add(1, "day").format("YYYY-MM-DD");
            const sun = d.add(2, "day").format("YYYY-MM-DD");

            // If we don't have the full weekend in range, shade what we can.
            if (set.has(k) && set.has(sat) && set.has(sun)) {
                areas.push([{ xAxis: k }, { xAxis: sun }]);
            } else if (set.has(k) && set.has(sat)) {
                areas.push([{ xAxis: k }, { xAxis: sat }]);
            } else if (set.has(k)) {
                areas.push([{ xAxis: k }, { xAxis: k }]);
            }
        }
        return areas;
    }, [chartDayKeys]);
    useEffect(() => {
        const inst = chartRef.current?.getEchartsInstance?.();
        if (!inst) return;

        const zr = inst.getZr();

        const computeSnap = (e) => {
            const pixel = [e?.offsetX, e?.offsetY];
            if (!inst.containPixel({ gridIndex: 0 }, pixel)) return null;

            const dataXY = inst.convertFromPixel(
                { xAxisIndex: 0, yAxisIndex: 0 },
                pixel,
            );
            if (!Array.isArray(dataXY) || dataXY.length < 2) return null;

            const xIdx = Math.round(Number(dataXY[0]));
            const yVal = Number(dataXY[1]);

            if (!Number.isFinite(xIdx)) return null;
            if (xIdx < 0 || xIdx >= chartDayKeys.length) return null;

            const dateKey = chartDayKeys[xIdx];
            if (!dateKey) return null;

            // Only allow manual targets inside the forecast window
            if (dayIdxByKey[dateKey] == null || dateKey < startDateStr)
                return null;

            const isAlt = Boolean(e?.event?.altKey);
            const hasAnchor = manualTargetsByDay?.[dateKey] != null;

            const wantsDelete = isAlt && hasAnchor;

            const pretty = formatChartDate(dateKey);
            const rawValue = wantsDelete
                ? Number(manualTargetsByDay[dateKey])
                : yVal;
            const value = Number.isFinite(rawValue)
                ? Math.max(0, Math.round(rawValue))
                : 0;

            const snapped = inst.convertToPixel(
                { xAxisIndex: 0, yAxisIndex: 0 },
                [dateKey, value],
            );
            if (!Array.isArray(snapped) || snapped.length < 2) return null;

            const intent = wantsDelete ? "delete" : "upsert";
            const label = wantsDelete
                ? `Delete ${pretty}: ${value}`
                : `${pretty}: ${value}`;

            return {
                dateKey,
                value,
                x: snapped[0],
                y: snapped[1],
                label,
                intent,
                canDelete: wantsDelete,
            };
        };

        const clearDraft = () => {
            draftTargetMarkerRef.current = null;
            setDraftTargetMarker(null);
        };

        const onMove = (e) => {
            if (!isTargetEditMode) return clearDraft();
            if (targetMode === TARGET_MODE_PERCENTAGE) return clearDraft();
            if (isTargetConfirmOpen) return;

            const snap = computeSnap(e);
            if (!snap) return clearDraft();

            const last = draftTargetMarkerRef.current;
            if (
                last &&
                last.dateKey === snap.dateKey &&
                last.value === snap.value
            )
                return;

            draftTargetMarkerRef.current = {
                dateKey: snap.dateKey,
                value: snap.value,
            };
            setDraftTargetMarker(snap);
        };

        const onClick = (e) => {
            if (!isTargetEditMode) return;
            if (targetMode === TARGET_MODE_PERCENTAGE) return;
            if (isTargetConfirmOpen) return;

            const snap = computeSnap(e);
            if (!snap) return;

            if (snap.intent === "delete") {
                setPendingTargetAction({
                    type: "delete",
                    dateKey: snap.dateKey,
                    value: Number(manualTargetsByDay[snap.dateKey]),
                });
                setIsTargetConfirmOpen(true);
                return;
            }

            setPendingTargetAction({
                type: "upsert",
                dateKey: snap.dateKey,
                value: snap.value,
            });
            setIsTargetConfirmOpen(true);
        };

        const onContextMenu = (e) => {
            if (!isTargetEditMode) return;
            if (targetMode === TARGET_MODE_PERCENTAGE) return;

            e?.event?.preventDefault?.();

            const snap = computeSnap(e);
            if (!snap) return;
            if (manualTargetsByDay?.[snap.dateKey] == null) return;

            setPendingTargetAction({
                type: "delete",
                dateKey: snap.dateKey,
                value: Number(manualTargetsByDay[snap.dateKey]),
            });
            setIsTargetConfirmOpen(true);
        };

        zr.on("mousemove", onMove);
        zr.on("mouseout", clearDraft);
        zr.on("click", onClick);
        zr.on("contextmenu", onContextMenu);

        return () => {
            const currentInst = chartRef.current?.getEchartsInstance?.();
            const currentZr = currentInst?.getZr?.();

            if (!currentZr) return;
            try {
                zr.off("mousemove", onMove);
                zr.off("mouseout", clearDraft);
                zr.off("click", onClick);
                zr.off("contextmenu", onContextMenu);
            } catch (error) {
                // Ignore cleanup errors from disposed zrender instance
            }
        };
    }, [
        startDateStr,
        chartDayKeys,
        dayIdxByKey,
        isTargetConfirmOpen,
        isTargetEditMode,
        manualTargetsByDay,
        targetMode,
    ]);

    // -------------------------
    // Chart (history + forecast)
    // -------------------------
    const chartOption = useMemo(() => {
        const formatChartValue = (value) =>
            Number.isFinite(value)
                ? fns(
                      hotelID,
                      value,
                      metric === REVENUE
                          ? { left: "currency", maxDecimals: 2 }
                          : { maxDecimals: 0 },
                  )
                : "—";
        const toChartValue = (v, allowZero = false) => {
            const n = Number(v);
            if (!Number.isFinite(n)) return null;
            if (!allowZero && n === 0) return null; // hide 0 unless explicitly allowed
            return n;
        };
        const xLabels = chartDayKeys;

        const anchorSet = new Set(Object.keys(manualTargetsByDay || {}));

        const targetWithDots = xLabels.map((k) => {
            const t = targetSeriesByDay[dayIdxByKey[k]];
            if (t == null) return null;

            const dotColor =
                !needsPredict && Number.isFinite(Number(predictedByDay[k]))
                    ? Number(predictedByDay[k]) >= t
                        ? GREEN_COLOR
                        : RED_COLOR
                    : TARGET_COLOR;

            const out = {
                value: toChartValue(t, true),
                itemStyle: { color: dotColor, borderColor: dotColor },
            };

            if (targetMode !== TARGET_MODE_PERCENTAGE && anchorSet.has(k)) {
                out.symbolSize = 9;
            }

            return out;
        });

        const weekendMarkArea = {
            silent: true,
            itemStyle: { color: "rgba(0,0,0,0.04)" },
            data: weekendMarkAreas,
        };

        const actualSeries = xLabels.map((k) => {
            if (!showHistoryActual) return null;
            return toChartValue(historyActualByDay[k], false);
        });

        const predictedSeries = xLabels.map((k) => {
            const isForecast = !dayjs
                .utc(k)
                .isBefore(forecastVisibleStartUtc, "day");
            if (!isForecast || needsPredict) return null;
            return toChartValue(predictedByDay[k], false);
        });

        const series = [];

        if (showHistoryActual) {
            series.push({
                name:
                    metric === REVENUE
                        ? "OTB Revenue"
                        : "OTB Confirmed bookings",
                type: "bar",
                stack: "forecast_stack",
                barMaxWidth: 35,
                data: actualSeries,
                itemStyle: { color: OTB_COLOR },
                markArea: weekendMarkArea,
            });
        }

        series.push({
            name: `Predicted ${metricLabel}`,
            type: "bar",
            stack: "forecast_stack",
            barMaxWidth: 35,
            data: predictedSeries,
            itemStyle: { color: PREDICTED_COLOR },
            ...(showHistoryActual ? {} : { markArea: weekendMarkArea }),
        });

        series.push({
            name: `Target ${metricLabel}`,
            type: "line",
            data: targetWithDots,
            smooth: true,
            symbol: "circle",
            showSymbol: true,
            showAllSymbol: true,
            symbolSize: 4,
            lineStyle: { width: 3, color: TARGET_COLOR },
            itemStyle: { color: TARGET_COLOR },
        });

        const isEditingTarget =
            isTargetEditMode && targetMode !== TARGET_MODE_PERCENTAGE;
        const tooltip = isEditingTarget
            ? { show: false }
            : {
                  trigger: "axis",
                  valueFormatter: formatChartValue,
              };

        const draftGraphic =
            isEditingTarget && draftTargetMarker
                ? [
                      {
                          id: "targetDraftPoint",
                          type: "group",
                          silent: true,
                          children:
                              draftTargetMarker.intent === "delete"
                                  ? [
                                        {
                                            type: "line",
                                            shape: {
                                                x1: draftTargetMarker.x - 7,
                                                y1: draftTargetMarker.y - 7,
                                                x2: draftTargetMarker.x + 7,
                                                y2: draftTargetMarker.y + 7,
                                            },
                                            style: {
                                                stroke: RED_COLOR,
                                                lineWidth: 2,
                                                opacity:
                                                    draftTargetMarker.canDelete
                                                        ? 1
                                                        : 0.35,
                                            },
                                        },
                                        {
                                            type: "line",
                                            shape: {
                                                x1: draftTargetMarker.x - 7,
                                                y1: draftTargetMarker.y + 7,
                                                x2: draftTargetMarker.x + 7,
                                                y2: draftTargetMarker.y - 7,
                                            },
                                            style: {
                                                stroke: RED_COLOR,
                                                lineWidth: 2,
                                                opacity:
                                                    draftTargetMarker.canDelete
                                                        ? 1
                                                        : 0.35,
                                            },
                                        },
                                        {
                                            type: "text",
                                            style: {
                                                text: draftTargetMarker.label,
                                                x: draftTargetMarker.x + 10,
                                                y: draftTargetMarker.y - 10,
                                                fill: "#111",
                                                backgroundColor:
                                                    "rgba(255,255,255,0.9)",
                                                padding: [2, 4],
                                                font: "12px sans-serif",
                                            },
                                        },
                                    ]
                                  : [
                                        {
                                            type: "circle",
                                            shape: {
                                                cx: draftTargetMarker.x,
                                                cy: draftTargetMarker.y,
                                                r: 6,
                                            },
                                            style: {
                                                fill: TARGET_COLOR,
                                                opacity: 0.9,
                                            },
                                        },
                                        {
                                            type: "text",
                                            style: {
                                                text: draftTargetMarker.label,
                                                x: draftTargetMarker.x + 10,
                                                y: draftTargetMarker.y - 10,
                                                fill: "#111",
                                                backgroundColor:
                                                    "rgba(255,255,255,0.9)",
                                                padding: [2, 4],
                                                font: "12px sans-serif",
                                            },
                                        },
                                    ],
                      },
                  ]
                : [];

        // y en el return final del option:
        return {
            grid: {
                left: 20,
                right: 20,
                top: 30,
                bottom: 55,
                containLabel: true,
            },
            tooltip,
            graphic: draftGraphic,
            legend: { top: 0 },
            xAxis: {
                type: "category",
                data: xLabels,
                axisTick: { alignWithLabel: true },
                axisLabel: { formatter: formatChartDate },
                axisPointer: {
                    label: { formatter: ({ value }) => formatChartDate(value) },
                },
            },
            yAxis: {
                type: "value",
                axisLabel: { formatter: formatChartValue },
            },
            series,
        };
    }, [
        chartDayKeys,
        forecastVisibleStartUtc,
        needsPredict,
        predictedByDay,
        historyActualByDay,
        lastYearMappedToCurrentDay,
        targetPct,
        weekendMarkAreas,
        showHistoryActual,
        historyEnd,
        dayKeys,
        dayIdxByKey,
        targetSeriesByDay,
        manualTargetsByDay,
        targetMode,
    ]);

    return (
        <Container maxWidth={false} sx={{ mt: 2 }}>
            <DataLoader Reservations SimpleData />
            <Typography variant="h4">Forecast Prediction</Typography>

            <Typography variant="subtitle1" color="initial" sx={{ mt: 1 }}>
                ⚠️This is a work in progress report. This is NOT a final
                product.
                <br />
                <br />
                See how many new bookings your hotel can expect and how much
                revenue those bookings are likely to generate. The forecast uses
                the date each reservation is made, regardless of when the guest
                arrives.
            </Typography>

            {trainedHotelsError && (
                <div style={{ marginTop: 12 }}>
                    <Alert severity="info">
                        Could not load trained hotels list. Showing all hotels.
                    </Alert>
                </div>
            )}

            {!isLoadingTrainedHotels &&
                !trainedHotelsError &&
                !isCurrentHotelTrained && (
                    <div style={{ marginTop: 12 }}>
                        <Alert severity="warning">
                            <AlertTitle>
                                Hotel not trained for forecast
                            </AlertTitle>
                            This hotel is not in the trained forecast SITES
                            list. Pick another one.
                        </Alert>
                    </div>
                )}
            <br />

            <Stack
                direction="row"
                spacing={2}
                useFlexGap
                flexWrap="wrap"
                sx={{ justifyContent: "flex-start", alignItems: "flex-end" }}
            >
                <ToggleButtonTitled
                    TITLE={"Predict"}
                    values={{
                        [REVENUE]: "Revenue",
                        [BOOKINGS]: "Bookings",
                    }}
                    valueSet={metric}
                    onChange={(e, v) => {
                        setMetric(v);
                        setPredictError(null);
                    }}
                    TITLE_TOOLTIP={
                        "Switch between different forecast metrics - Revenue, or Bookings."
                    }
                />

                {currentHotelSiteIDs.length > 1 && (
                    <Stack>
                        <Typography>Site</Typography>
                        <Select
                            value={selectedSiteId}
                            onChange={(e) => setSelectedSiteId(e.target.value)}
                            sx={{ maxHeight: 50, minWidth: 120 }}
                        >
                            {currentHotelSiteIDs.map((sid) => (
                                <MenuItem key={sid} value={sid}>
                                    {sid}
                                </MenuItem>
                            ))}
                        </Select>
                    </Stack>
                )}

                <Stack>
                    <Typography>Model</Typography>
                    <Select
                        value={modelName}
                        onChange={(e) => setModelName(e.target.value)}
                        sx={{ maxHeight: 50, minWidth: 160 }}
                        disabled={
                            isLoadingModels || availableModels.length === 0
                        }
                    >
                        <MenuItem value={AUTO_MODEL}>
                            auto ({autoModelResolved || "—"})
                        </MenuItem>
                        {availableModels.map((m) => (
                            <MenuItem key={m} value={m}>
                                {m}
                            </MenuItem>
                        ))}
                    </Select>
                </Stack>

                <DateRangePicker
                    title="Forecast Period"
                    tittle_tooltip="Choose the date range to forecast (start_date + horizon_days)."
                    isRangePicker={true}
                    // NEW: presets wiring (same as Pickup)
                    presets={forecastPresetOptions}
                    presetValue={forecastPreset}
                    onChangePresetValue={(v) => setForecastPreset(v)}
                    customPreset={{ value: "CUSTOM", label: "Custom" }}
                    onChangeFromDate={(d) => setFromDate(d)}
                    onChangeToDate={(d) => setToDate(d)}
                    fromDateProps={{
                        value: fromDate,
                        minDate: minForecastUtc,
                        maxDate: maxForecastUtc,
                    }}
                    toDateProps={{
                        value: toDate,
                        minDate: minForecastUtc,
                        maxDate: maxForecastUtc,
                    }}
                />
                <Stack>
                    <Tooltip
                        title="Choose the comparison used for the summary and percentage-based targets."
                        placement="top"
                        arrow
                    >
                        <Typography>Comparison</Typography>
                    </Tooltip>

                    <Select
                        value={comparison}
                        onChange={(e) => setComparison(e.target.value)}
                        sx={{ maxHeight: 50, minWidth: 180 }}
                        inputProps={{ "aria-label": "Comparison" }}
                    >
                        {[
                            COMPARISON_PREVIOUS_YEAR,
                            COMPARISON_SAME_TIME_LAST_YEAR,
                        ].map((key) => (
                            <MenuItem key={key} value={key}>
                                {COMPARISON_FILTER[key].label}
                            </MenuItem>
                        ))}
                    </Select>
                </Stack>
                <Stack>
                    <Tooltip
                        title={
                            "Choose how the Target line is generated: percentage, manual points, or a combination of both."
                        }
                        placement={"top"}
                        arrow
                    >
                        <Typography>Target mode</Typography>
                    </Tooltip>

                    <Select
                        value={targetMode}
                        onChange={(e) => {
                            setTargetMode(e.target.value);
                            setIsTargetEditMode(false);
                        }}
                        sx={{ maxHeight: 50, minWidth: 160 }}
                    >
                        <MenuItem value={TARGET_MODE_PERCENTAGE}>
                            Percentage
                        </MenuItem>
                        <MenuItem value={TARGET_MODE_MANUAL}>Manual</MenuItem>
                        <MenuItem value={TARGET_MODE_COMBINED}>
                            Combined
                        </MenuItem>
                    </Select>
                </Stack>

                {targetMode !== TARGET_MODE_MANUAL && (
                    <Stack>
                        <Tooltip
                            title={`Set your target growth % relative to ${comparisonLabel}.`}
                            placement={"top"}
                            arrow
                        >
                            <Typography>Target %</Typography>
                        </Tooltip>

                        <TextField
                            value={targetPct}
                            onChange={(e) => {
                                const raw = Number(e.target.value);
                                const val = Number.isFinite(raw) ? raw : 0;
                                const clamped = Math.max(
                                    -50,
                                    Math.min(50, val),
                                );
                                setTargetPct(clamped);
                            }}
                            inputProps={{
                                inputMode: "numeric",
                                pattern: "-?\\d*",
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        %
                                    </InputAdornment>
                                ),
                            }}
                            size="small"
                            sx={{
                                maxWidth: 80,
                                minWidth: 60,
                                "& .MuiInputBase-root": { height: 50 },
                                "& .MuiOutlinedInput-input": {
                                    paddingTop: 0.75,
                                    paddingBottom: 0.75,
                                },
                                "& .MuiInputAdornment-root": {
                                    height: "100%",
                                    m: 0,
                                },
                            }}
                        />
                    </Stack>
                )}

                {targetMode !== TARGET_MODE_PERCENTAGE && (
                    <Stack sx={{ height: 50, justifyContent: "flex-end" }}>
                        <Button
                            variant={
                                isTargetEditMode ? "contained" : "outlined"
                            }
                            onClick={() => setIsTargetEditMode((v) => !v)}
                            disabled={
                                !userIdForTargets || isLoadingManualTargets
                            }
                            sx={{ height: 50 }}
                        >
                            {isTargetEditMode ? "Exit editing" : "Edit target"}
                        </Button>
                    </Stack>
                )}
                <HotelSelector
                    compact
                    limitedIds={
                        Array.isArray(hotelsAllowedIds) &&
                        hotelsAllowedIds.length > 0
                            ? hotelsAllowedIds
                            : undefined
                    }
                />
            </Stack>
            {(isPredicting || isLoadingModels) && (
                <Alert
                    severity="info"
                    sx={{ mt: 2 }}
                    icon={<CircularProgress size={18} />}
                >
                    {isPredicting
                        ? "Generating forecast automatically…"
                        : "Loading forecast models…"}
                </Alert>
            )}

            {!isPredicting &&
                !isLoadingModels &&
                !predictError &&
                !modelsError &&
                needsPredict &&
                (!auth?.user?.jwt ||
                    !selectedSiteId ||
                    availableModels.length === 0) && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {!auth?.user?.jwt
                            ? "Forecast unavailable: missing authentication."
                            : !selectedSiteId
                              ? "Forecast unavailable: no SiteID found for this hotel."
                              : "Forecast unavailable: no trained models available for this metric."}
                    </Alert>
                )}
            {modelsError && (
                <div style={{ marginTop: 12 }}>
                    <Alert
                        severity={"warning"}
                        onClose={() => {
                            setModelsError(null);
                            setPredictError(null);
                        }}
                    >
                        <AlertTitle>"Models list warning"</AlertTitle>
                        {modelsError}
                    </Alert>
                </div>
            )}

            {predictError && (
                <div style={{ marginTop: 12 }}>
                    <Alert
                        severity="error"
                        onClose={() => setPredictError(null)}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handlePredict}
                                disabled={isPredicting}
                            >
                                {isPredicting ? "Retrying…" : "Retry"}
                            </Button>
                        }
                    >
                        <AlertTitle>Prediction failed</AlertTitle>
                        {predictError}
                    </Alert>
                </div>
            )}

            <div style={{ marginTop: 16 }}>
                <ReportTableMetricsSummary
                    title="Period summary"
                    data={summaryData}
                    js={summaryJs}
                    hideExtra={true}
                    hideEvents={true}
                    tooltipRenderers={{
                        [actualLabel]: ({ valueOld }) => {
                            const description =
                                metric === BOOKINGS
                                    ? "Number of Special Offer reservations already made during the selected period, regardless of arrival date."
                                    : "Total value of Special Offer reservations already made during the selected period, grouped by booking date.";

                            if (valueOld == null) return description;

                            return `${description} Reporting ${comparisonLabel} was ${fns(
                                hotelID,
                                valueOld,
                                metric === BOOKINGS
                                    ? { maxDecimals: 0 }
                                    : { left: "currency", maxDecimals: 0 },
                            )}.`;
                        },
                        [stlyLabel]: () =>
                            `${metric === BOOKINGS ? "Room bookings" : "Room revenue plus extras"} recorded during the equivalent booking period last year${
                                comparison === COMPARISON_SAME_TIME_LAST_YEAR
                                    ? ", capped at this date last year"
                                    : ", including the full selected period"
                            }. Includes cancellations.`,
                        [predictedLabel]: () =>
                            metric === BOOKINGS
                                ? "Expected number of new bookings. Adds the daily booking forecasts for today and any later dates in the selected period."
                                : "Expected revenue from new bookings. Adds the daily revenue forecasts for today and any later dates in the selected period.",
                        [otbPlusPredicted]: () =>
                            metric === BOOKINGS
                                ? "Bookings already recorded in the selected period plus predicted bookings for the forecast dates."
                                : "Revenue already recorded in the selected period plus predicted revenue for the forecast dates.",
                        [targetLabel]: () =>
                            targetMode === TARGET_MODE_MANUAL
                                ? "Sum of the daily targets for the selected period. Values between manual entries are interpolated. Before the first entry and after the last, the nearest entered value is used."
                                : targetMode === TARGET_MODE_COMBINED
                                  ? `Sum of the daily targets. Manual values apply on their dates; other dates use ${comparisonLabel} adjusted by the target percentage (${targetPct}%).`
                                  : `Target ${metricLabel.toLowerCase()} = ${stlyLabel} x (1 + ${targetPct} / 100), rounded to the nearest whole number.`,

                        [varianceStlyLabel]: () =>
                            `Percentage above or below ${stlyLabel}. Calculated as ((OTB + Predicted) - ${comparisonLabel}) / ${comparisonLabel} x 100.`,

                        [varianceTargetLabel]: () =>
                            "Percentage above or below the target for the selected period. Calculated as ((OTB + Predicted) - Target) / Target x 100.",
                    }}
                    metrics={Object.fromEntries(
                        Object.entries(summaryData.group0.result).map(
                            ([name, value]) => [
                                name,
                                {
                                    short: name,
                                    format: !Number.isFinite(value)
                                        ? {}
                                        : name.includes("(%)")
                                          ? { right: "%", maxDecimals: 2 }
                                          : metric === REVENUE
                                            ? {
                                                  left: "currency",
                                                  maxDecimals: 2,
                                              }
                                            : { maxDecimals: 0 },
                                    valueElement:
                                        Number.isFinite(value) &&
                                        (name === varianceStlyLabel ||
                                            name === varianceTargetLabel) ? (
                                            <SmallPercentage
                                                actual={100 + value}
                                                old={100}
                                                brackets={false}
                                                tooltip={false}
                                                bubble
                                            />
                                        ) : undefined,
                                },
                            ],
                        ),
                    )}
                />
            </div>

            <div style={{ height: 320, marginTop: 16 }}>
                <EChartsReact
                    ref={chartRef}
                    style={{ height: "100%" }}
                    option={chartOption}
                    notMerge={true}
                />
                <ConfirmModal
                    isOpen={isTargetConfirmOpen}
                    setIsOpen={(open) => {
                        setIsTargetConfirmOpen(open);
                        if (!open) setPendingTargetAction(null);
                    }}
                    text={
                        pendingTargetAction?.type === "delete"
                            ? "Delete target point?"
                            : "Save target point?"
                    }
                    subtext={
                        pendingTargetAction
                            ? `${pendingTargetAction.dateKey}: ${pendingTargetAction.value}`
                            : ""
                    }
                    acceptText={
                        pendingTargetAction?.type === "delete"
                            ? "Delete"
                            : "Save"
                    }
                    declineText="Cancel"
                    onAccept={() => {
                        const a = pendingTargetAction;
                        if (!a) return;

                        if (a.type === "delete")
                            deleteManualTargetPoint(a.dateKey);
                        else upsertManualTargetPoint(a.dateKey, a.value);

                        setPendingTargetAction(null);
                    }}
                    onDecline={() => setPendingTargetAction(null)}
                />{" "}
            </div>
        </Container>
    );
};

export default ForecastScreen;
